/**
 * FailureRadarCard (iter86.66) — Maestro card surfacing
 * memory/PROCESS_FAILURES.md entries so process/agent-drift signals
 * show in the console early. Open entries render loud (red); resolved
 * history collapses behind a details toggle. Read-only — the markdown
 * file stays the source of truth.
 */
import { useEffect, useState } from "react";
import { Siren } from "lucide-react";
import { api } from "@/lib/api";

const SEV_PILL = {
    S1: "bg-red-900/40 text-red-300 border-red-500",
    S2: "bg-amber-900/40 text-amber-300 border-amber-500",
    S3: "bg-zinc-800 text-zinc-400 border-zinc-600",
};

function FailureRow({ e }) {
    const open = e.status === "open";
    return (
        <details className="border-t border-zinc-800 pt-1.5" data-testid={`failure-radar-row-${e.id}`}>
            <summary className="flex items-center gap-2 flex-wrap text-[11px] cursor-pointer list-none">
                <span className={`pill text-[9px] font-bold uppercase border ${open ? "bg-red-900/40 text-red-300 border-red-500" : "bg-zinc-900 text-zinc-500 border-zinc-700"}`}>
                    {open ? "open" : "resolved"}
                </span>
                <span className={`pill text-[9px] font-bold border ${SEV_PILL[e.severity] || SEV_PILL.S3}`}>{e.severity}</span>
                <span className="font-bold text-white">{e.id}</span>
                <span className={open ? "text-zinc-200" : "text-zinc-400"}>{e.title}</span>
                {e.date_caught && <span className="text-zinc-600">{e.date_caught}</span>}
            </summary>
            <pre className="text-[10px] text-zinc-400 whitespace-pre-wrap mt-1.5 ml-1 max-h-56 overflow-y-auto font-mono">{e.body}</pre>
        </details>
    );
}

export const FailureRadarCard = () => {
    const [data, setData] = useState(null);
    const [showResolved, setShowResolved] = useState(false);
    useEffect(() => {
        api.releaseOpsProcessFailures().then(setData).catch(() => setData(null));
    }, []);
    if (!data || !data.entries?.length) return null;
    const open = data.entries.filter((e) => e.status === "open");
    const resolved = data.entries.filter((e) => e.status !== "open");
    return (
        <div
            className={`border rounded p-3 mb-4 space-y-2 ${open.length ? "border-red-800 bg-red-950/10" : "border-zinc-800 bg-zinc-950/40"}`}
            data-testid="failure-radar-card"
        >
            <p className={`font-bold text-xs uppercase flex items-center gap-1.5 ${open.length ? "text-red-200" : "text-zinc-300"}`}>
                <Siren className="w-3.5 h-3.5" />
                Failure Radar
                <span className={`pill text-[9px] border ${open.length ? "bg-red-900/40 text-red-300 border-red-500" : "bg-zinc-900 text-zinc-500 border-zinc-700"}`} data-testid="failure-radar-open-count">
                    {open.length} open
                </span>
                <span className="text-[9px] text-zinc-600 normal-case font-normal">· {resolved.length} resolved</span>
            </p>
            <p className="text-[10px] text-zinc-500">
                Major process & agent-behavior failures from <code>{data.source}</code>. Entries without an
                explicit <b>Status</b> bullet count as OPEN until the countermeasure ships and the doc is updated.
            </p>
            {open.map((e) => <FailureRow key={e.id} e={e} />)}
            {resolved.length > 0 && (
                <button
                    type="button"
                    onClick={() => setShowResolved((v) => !v)}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300 underline"
                    data-testid="failure-radar-toggle-resolved"
                >
                    {showResolved ? "Hide" : "Show"} {resolved.length} resolved
                </button>
            )}
            {showResolved && resolved.map((e) => <FailureRow key={e.id} e={e} />)}
        </div>
    );
};
